// src/pages/admin/emails/components/TemplateForm.tsx
import { useEffect, useState } from "react";
import { supabase } from "../../../../lib/supabaseClient";

type Props = {
  initial?: {
    id: string;
    name: string;
    subject: string;
    html_body: string;
    text_body: string;
    enabled: boolean;
    allow_tracking: boolean;
    dedupe_period: string;
    category: string;
  };
  onClose: () => void;
  onSaved: () => void;
};

const DEFAULT_HTML = `<div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;line-height:1.5;font-size:16px;color:#111">
  <h1 style="font-size:20px;margin:0 0 12px">Hallo {{first_name}},</h1>
  <p>Das ist eine Beispielvorlage. Deine Reise <strong>{{trip_name}}</strong> wartet!</p>
  <p style="margin:16px 0">
    <a href="{{cta_url}}" style="display:inline-block;padding:10px 14px;border-radius:8px;text-decoration:none;background:#4f46e5;color:#fff">
      Jetzt öffnen
    </a>
  </p>
  <hr style="border:none;border-top:1px solid #eee;margin:16px 0"/>
  <p style="font-size:12px;color:#555">Footer/Impressum/Abmelden …</p>
</div>`;

const DEFAULT_TEXT = `Hallo {{first_name}},

Das ist eine Beispielvorlage. Deine Reise "{{trip_name}}" wartet!

Öffnen: {{cta_url}}

Footer/Impressum/Abmelden …`;

export default function TemplateForm({ initial, onClose, onSaved }: Props) {
  const isEdit = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name ?? "");
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [htmlBody, setHtmlBody] = useState(initial?.html_body ?? DEFAULT_HTML);
  const [textBody, setTextBody] = useState(initial?.text_body ?? DEFAULT_TEXT);
  const [enabled, setEnabled] = useState(initial?.enabled ?? false);
  const [allowTracking, setAllowTracking] = useState(initial?.allow_tracking ?? false);
  const [dedupePeriod, setDedupePeriod] = useState(initial?.dedupe_period ?? "7 days");
  const [category, setCategory] = useState(initial?.category ?? "onboarding");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setErr(null);
  }, [name, subject, htmlBody, textBody, enabled, allowTracking, dedupePeriod, category]);

  async function save() {
    setSaving(true);
    setErr(null);

    if (!name.trim() || !subject.trim()) {
      setErr("Name und Betreff sind Pflichtfelder.");
      setSaving(false);
      return;
    }

    const payload = {
      name: name.trim(),
      subject: subject.trim(),
      html_body: htmlBody,
      text_body: textBody,
      enabled,
      allow_tracking: allowTracking,
      dedupe_period: dedupePeriod.trim(),
      category: category.trim(),
    };

    let error;
    if (isEdit) {
      const res = await supabase.from("email_templates").update(payload).eq("id", initial!.id);
      error = res.error ?? null;
    } else {
      const res = await supabase.from("email_templates").insert(payload);
      error = res.error ?? null;
    }

    setSaving(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onSaved();
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end md:items-center justify-center z-50">
      <div className="bg-[#161b27] border border-[#2a3347] w-full md:max-w-3xl max-h-[95vh] rounded-t-2xl md:rounded-2xl shadow-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#2a3347] flex items-center justify-between">
          <h3 className="font-semibold text-slate-100">
            {isEdit ? "Vorlage bearbeiten" : "Neue Vorlage erstellen"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
            ✕
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto max-h-[75vh]">
          {err && (
            <div className="p-3 bg-red-950/20 text-red-400 border border-red-800/30 rounded">
              {err}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <div className="text-sm text-slate-400 mb-1">Name*</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="onboarding_zero_progress_de"
                className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
              />
            </label>

            <label className="block">
              <div className="text-sm text-slate-400 mb-1">Kategorie</div>
              <input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="onboarding"
                className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
              />
            </label>
          </div>

          <label className="block">
            <div className="text-sm text-slate-400 mb-1">Betreff*</div>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Willkommen bei Svevana 👋 – So geht's weiter"
              className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
            />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <div className="text-sm text-slate-400 mb-1">Dedupe-Fenster (Text)</div>
              <input
                value={dedupePeriod}
                onChange={(e) => setDedupePeriod(e.target.value)}
                placeholder="7 days"
                className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
              />
              <div className="text-xs text-slate-500 mt-1">
                Beispiele: <code className="text-sky-400">1 day</code>, <code className="text-sky-400">7 days</code>, <code className="text-sky-400">14 days</code>
              </div>
            </label>

            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="accent-sky-500"
                />
                <span className="text-slate-300">Aktiv</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allowTracking}
                  onChange={(e) => setAllowTracking(e.target.checked)}
                  className="accent-sky-500"
                />
                <span className="text-slate-300">Tracking erlauben</span>
              </label>
            </div>
          </div>

          <label className="block">
            <div className="text-sm text-slate-400 mb-1">HTML-Body</div>
            <textarea
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded font-mono outline-none focus:border-sky-400"
            />
            <div className="text-xs text-slate-500 mt-1">
              Platzhalter: <code className="text-sky-400">{`{{first_name}}`}</code>,{" "}
              <code className="text-sky-400">{`{{trip_name}}`}</code>, <code className="text-sky-400">{`{{cta_url}}`}</code>
            </div>
          </label>

          <label className="block">
            <div className="text-sm text-slate-400 mb-1">Text-Body</div>
            <textarea
              value={textBody}
              onChange={(e) => setTextBody(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded font-mono outline-none focus:border-sky-400"
            />
          </label>
        </div>

        <div className="px-4 py-3 border-t border-[#2a3347] flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors">
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-60 transition-colors"
          >
            {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
